import React, { useState, useEffect } from "react";
import { KeyboardArrowDown, KeyboardArrowUp } from "@material-ui/icons";
import {
  Box,
  Collapse,
  styled,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {
  BatchAndExpiryManagementRowsData,
  ColumnMetaData,
  RealTimeStockRowsData,
  TransformedBatch,
} from "../../../types/types";
import MultiQtyCell from "../../../components/dms_common/MultiQtyCell";
import { getCasePieceSumProps } from "../getQuantityProps";
import ExpiryDateCell from "../../../components/dms_common/ExpiryDateCell";
import { getDataFromLocalDb } from "../../../services/dbService";
import { TruncatedTextUptoLine } from "../../../components/dms_common/DmsUtils";

const CustomTableCell = styled(TableCell)`
  color: #909090;
  font-weight: 500;
  font-size: 13px;
  padding: 12px 0px;
`;

enum StockOptions {
  committedQty = "Committed Stock",
  availableQty = "Available Stock",
  unsellableQty = "Unsellable Stock",
  returnedQty = "Returned Stock",
  onRouteQty = "On Route Stock",
}

interface ExpandableTableRowProps {
  row: RealTimeStockRowsData;
  columnsMetaData: ColumnMetaData<RealTimeStockRowsData>[];
  selectedOption: string;
  expandedRowIds: string[];
  handleChangeExpandedRows: Function;
  filterParam?: string | null;
  stockTypeParam?: string | null;
}

const ExpandableTableRow: React.FC<ExpandableTableRowProps> = ({
  row,
  columnsMetaData,
  selectedOption,
  expandedRowIds,
  handleChangeExpandedRows,
  filterParam,
  stockTypeParam,
}) => {
  const [filteredExpandableRowData, setFilteredExpandableRowData] = useState<
    BatchAndExpiryManagementRowsData[]
  >([]);

  const stockOptionDisplayValue = StockOptions[selectedOption as keyof typeof StockOptions];
  const [expirySortOrder, setExpirySortOrder] = useState<"asc" | "desc">("asc");

  // highlight row if it is out of stock(coming from home page)
  const shouldHighlightRow = () => {
    if (filterParam === "outOfStock" && stockTypeParam === "availableQty") {
      return row.pieceAvailableQty === 0 && row.caseAvailableQty === 0 && row.otherAvailableQty === 0;
    }
    return false;
  };

  const setSkuBatchData = async () => {
    const filteredData = await getDataFromLocalDb("BatchWiseStockData", { skuCode: row.skuCode });
    let batchIdWiseStockData: BatchAndExpiryManagementRowsData[] = [];
    filteredData.data.forEach((item: TransformedBatch) => {
      batchIdWiseStockData.push({
        id: item.batchId + item.skuCode,
        skuCode: item.skuCode,
        batchId: item.batchId,
        adjustmentDate:
          item.caseQty?.lastModifiedTime ??
          item.pieceQty?.lastModifiedTime ??
          item.otherQty?.lastModifiedTime ??
          "",
        expiryDate:
          item.caseQty?.expiryDate ?? item.pieceQty?.expiryDate ?? item.otherQty?.expiryDate ?? "",
        skuDescription: "",
        shelfLife: row.shelfLife,
        pieceCommittedQty: item.pieceQty?.committedQty ?? 0,
        pieceAvailableQty: item.pieceQty?.availableQty ?? 0,
        pieceUnsellableQty: item.pieceQty?.unsellableQty ?? 0,
        pieceInTransitQty: item.pieceQty?.inTransitQty ?? 0,
        pieceOnRouteQty: item.pieceQty?.onRouteQty ?? 0,
        pieceReturnedQty: item.pieceQty?.returnedQty ?? 0,
        caseCommittedQty: item.caseQty?.committedQty ?? 0,
        caseAvailableQty: item.caseQty?.availableQty ?? 0,
        caseUnsellableQty: item.caseQty?.unsellableQty ?? 0,
        caseInTransitQty: item.caseQty?.inTransitQty ?? 0,
        caseOnRouteQty: item.caseQty?.onRouteQty ?? 0,
        caseReturnedQty: item.caseQty?.returnedQty ?? 0,
        otherCommittedQty: item.otherQty?.committedQty ?? 0,
        otherAvailableQty: item.otherQty?.availableQty ?? 0,
        otherUnsellableQty: item.otherQty?.unsellableQty ?? 0,
        otherInTransitQty: item.otherQty?.inTransitQty ?? 0,
        otherOnRouteQty: item.otherQty?.onRouteQty ?? 0,
        otherReturnedQty: item.otherQty?.returnedQty ?? 0,
      });
    });
    setFilteredExpandableRowData(batchIdWiseStockData);
  };

  const sortedData = [...filteredExpandableRowData].sort((a, b) => {
    const dateA = new Date(a.expiryDate || "").getTime();
    const dateB = new Date(b.expiryDate || "").getTime();
    return expirySortOrder === "asc" ? dateA - dateB : dateB - dateA;
  });

  const toggleExpirySortOrder = () => {
    setExpirySortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
  };
  useEffect(() => {
    if (expandedRowIds.includes(row.id)) {
      setSkuBatchData();
    }
  }, [expandedRowIds, row.skuCode]);

  const getQtyValuesBatch = (rowData: BatchAndExpiryManagementRowsData | RealTimeStockRowsData) => {
    let pcQty = 0;
    let csQty = 0;
    let otherQty = 0;
    switch (selectedOption) {
      case "availableQty": {
        pcQty = rowData.pieceAvailableQty;
        csQty = rowData.caseAvailableQty;
        otherQty = rowData.otherAvailableQty;
        break;
      }
      case "committedQty": {
        pcQty = rowData.pieceCommittedQty;
        csQty = rowData.caseCommittedQty;
        otherQty = rowData.otherCommittedQty;
        break;
      }
      case "unsellableQty": {
        pcQty = rowData.pieceUnsellableQty;
        csQty = rowData.caseUnsellableQty;
        otherQty = rowData.otherUnsellableQty;
        break;
      }
      case "returnedQty": {
        pcQty = rowData.pieceReturnedQty;
        csQty = rowData.caseReturnedQty;
        otherQty = rowData.otherReturnedQty;
        break;
      }
      case "inTransitQty": {
        pcQty = rowData.pieceInTransitQty;
        csQty = rowData.caseInTransitQty;
        otherQty = rowData.otherInTransitQty;
        break;
      }
      case "onRouteQty": {
        pcQty = rowData.pieceOnRouteQty;
        csQty = rowData.caseOnRouteQty;
        otherQty = rowData.otherOnRouteQty;
        break;
      }
      default:
        break;
    }
    return {
      pcQty: pcQty,
      csQty: csQty,
      otherQty: otherQty,
    };
  };

  return (
    <>
      <TableRow style={{ backgroundColor: shouldHighlightRow() ? "#fff9f9" : "white" }}>
        {columnsMetaData.map((column, index) => {
          if (column.headerName.toLowerCase() === "batch id") {
            return (
              <CustomTableCell
                key={index}
                sx={{
                  paddingRight: column?.paddingRight ?? 0,
                  borderBottom: expandedRowIds.includes(row.id) ? "0px !important" : "1px solid #eaeaea",
                }}
              >
                <Box
                  className="batch-id-box"
                  onClick={() => handleChangeExpandedRows(row.id)}
                  sx={{
                    cursor: "pointer",
                    marginRight: "auto",
                    backgroundColor: "#F7F7F7",
                    width: "120px",
                  }}
                >
                  <Box sx={{ color: "#909090", fontSize: "11px" }}>
                    View batch IDs
                  </Box>
                  <div
                    className="icon-border-circle"
                    style={{ borderColor: "#909090" }}
                  >
                    {expandedRowIds.includes(row.id) ? (
                      <KeyboardArrowUp
                        style={{
                          fontSize: "13px",
                          color: "#909090",
                        }}
                      />
                    ) : (
                      <KeyboardArrowDown style={{ fontSize: "13px" }} />
                    )}
                  </div>
                </Box>
              </CustomTableCell>
            );
          } else if (column.headerName.toLowerCase() === "total stock") {
            return (
              <CustomTableCell
                key={index}
                sx={{
                  color: "#909090",
                  alignContent: "center",
                  paddingLeft: column?.paddingLeft ?? 0,
                  borderBottom: expandedRowIds.includes(row.id) ? "0px !important" : "1px solid #eaeaea",
                }}
              >
                <MultiQtyCell {...getCasePieceSumProps(row)} otherUnitName={row.otherUnitName}/>
              </CustomTableCell>
            );
          } else if (
            column.headerName.toLowerCase().includes("stock") &&
            column.headerName.toLowerCase() !== "total stock"
          ) {
            return (
              <CustomTableCell
                key={index}
                sx={{ color: "#909090", borderBottom: expandedRowIds.includes(row.id) ? "0px !important" : "1px solid #eaeaea" }}  
              >
                <MultiQtyCell {...getQtyValuesBatch(row)} otherUnitName={row.otherUnitName}/>
              </CustomTableCell>
            );
          } else {
            const cell = row[column.mappedValue]

            return (
              <CustomTableCell
                key={index}
                sx={{
                  color: "#909090",
                  width: column.width,
                  borderBottom: expandedRowIds.includes(row.id) ? "0px !important" : "1px solid #eaeaea",
                  paddingLeft: column?.paddingLeft ?? 0,
                }}
              >
                <Tooltip title={row[column.mappedValue]?.toString()} arrow>
                  <span style={{...TruncatedTextUptoLine(column?.truncateTextAfterLine || 1), paddingRight: "4px"}}>{cell}</span>
                </Tooltip>
              </CustomTableCell>
            );
          }
        })}
        <CustomTableCell
          sx={{
            color: "#909090",
            width: "200px",
            textAlign: "center",
            paddingLeft: "6px",
            borderBottom: expandedRowIds.includes(row.id) ? "0px !important" : "1px solid #eaeaea"
          }}
        >
          <MultiQtyCell {...getQtyValuesBatch(row)} otherUnitName={row.otherUnitName} />
        </CustomTableCell>
      </TableRow>
      {expandedRowIds.includes(row.id) && (
        <TableRow
          sx={{
            "& .MuiTableRow-root": {
              border: "none",
            },
            "& .MuiTableCell-head": {
              border: "none",
            },
          }}
        >
          <TableCell
            style={{
              paddingBottom: "8px",
              paddingTop: 0,
              paddingLeft: "20px",
              paddingRight: "8px",
            }}
            colSpan={columnsMetaData.length + 1}
          >
            <Collapse in={expandedRowIds.includes(row.id)} timeout="auto" unmountOnExit>
              {filteredExpandableRowData.length === 0 ? (
                <Box margin={0} sx={{ width: "100%", paddingLeft: "12px", borderLeft: "3px solid rgb(191, 191, 191)"}}>
                  <div className="batch-stock-table-wrapper" style={{borderRadius: "4px", overflow: "hidden", border: "1px solid #eaeaea"}}>
                  <Table size="small" aria-label="purchases" className="expanded-table"  sx={{ border: 0 }}>
                    <TableBody>
                      <TableCell sx={{ color: "#6F6F6F", width: "100%", textAlignLast: "center" }}>
                        No Data Available
                      </TableCell>
                    </TableBody>
                  </Table>
                  </div>
                </Box>
              ) : (
                <Box margin={0} sx={{ width: "100%", paddingLeft: "12px", borderLeft: "3px solid rgb(191, 191, 191)"}}>
                  <div className="batch-stock-table-wrapper" style={{borderRadius: "4px", overflow: "hidden", border: "1px solid #eaeaea"}}>
                  <Table size="small" aria-label="purchases" className="expanded-table"  sx={{ border: 0 }}>
                    <TableHead>
                      <TableRow style={{ backgroundColor: "#FAFAFA" }}>
                        <TableCell sx={{ color: "#6F6F6F", width: "25%" }}>Batch ID</TableCell>
                        <TableCell
                          sx={{ 
                            color: "#6F6F6F", 
                            width: "25%",
                          }}
                          onClick={toggleExpirySortOrder}
                        >
                          <div style={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            
                          }}>
                            <p style={{ whiteSpace: "nowrap", height: "33px", paddingTop: "6px", color: "#6F6F6F", fontWeight: "inherit", fontSize: "0.875rem" }}>Expiry Date</p>
                            <div 
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                lineHeight: "1.5rem",
                            }}>
                              {expirySortOrder === "asc" && <ArrowDropUpIcon style={{ }} />}
                              {expirySortOrder === "desc" && <ArrowDropDownIcon style={{  }} />}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell
                          sx={{
                            color: "#6F6F6F",
                            textAlign: "center",
                            width: "25%",
                          }}
                        >
                          Total Stock
                        </TableCell>
                        <TableCell
                          sx={{
                            color: "#6F6F6F",
                            textAlign: "center",
                            width: "25%",
                          }}
                        >
                          {stockOptionDisplayValue}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedData.map((batch, idx) => (
                        <TableRow key={idx}>
                          <TableCell
                            sx={{
                              color: "#909090",
                              fontSize: "13px",
                              paddingLeft: "0px",
                            }}
                          >
                            {batch.batchId.includes("unassigned")
                              ? "Unassigned Qty."
                              : batch.batchId}
                          </TableCell>
                          <TableCell sx={{ paddingLeft: "0px", textAlign: "left" }}>
                            <div style={{ width: "90px", maxWidth: "90px" }}>
                              <ExpiryDateCell
                                expiryDate={batch.expiryDate}
                                shelfLife={batch.shelfLife}
                              />
                            </div>
                          </TableCell>
                          <TableCell>
                            <MultiQtyCell {...getCasePieceSumProps(batch)} otherUnitName={row.otherUnitName} />
                          </TableCell>
                          <TableCell>
                            <MultiQtyCell {...getQtyValuesBatch(batch)} otherUnitName={row.otherUnitName} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  </div>
                </Box>
              )}
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

export default ExpandableTableRow;
